import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../components/BackButton";
import Breadcrumbs from "../components/Breadcrumbs";
import SearchSelect from "../components/SearchSelect";
import FloatingInput from "../components/FloatingInput";
import FloatingTextarea from "../components/FloatingTextarea";
import Button from "../components/Button";
import Switch from "../components/Switch";
import "./scss/seller-product-form.scss";
import MultiSearchSelect from "../components/MultiSearchSelect";
import api from "../api";
import SingleSearchSelect from "../components/SingleSearchSelect";
import Icon from "../lib/icon";
import { useToast } from "../contexts/ToastContext";
import ApiImage from "../components/ApiImage";

export default function SellerProductFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marketPercent, setMarketPercent] = useState(0);
  const { showToast } = useToast();

  const [form, setForm] = useState({
    isActive: true,
    category: [],
    subcategory: [],
    name: "",
    description: "",
    quantity: "",
    isCombine: false,
    cost: "",
    isDiscount: false,
    costDiscount: "",
    filters: {},
    customParameters: [],
    status: null,
    comment: null,
  });

  function handleChange(key, value) {
    setForm(prev => {
      const next = { ...prev, [key]: value };

      if (key === "category") {
        next.subcategory = [];
        next.filters = {};
      }

      return next;
    });
  }

  const finalPrice = useMemo(() => {
    const base = form.isDiscount
      ? Number(form.costDiscount)
      : Number(form.cost);

    if (!base) return 0;

    return Math.round(base + (base * marketPercent / 100));
  }, [form.cost, form.costDiscount, form.isDiscount, marketPercent]);

  async function handleSubmit() {
    if (form.isDiscount && Number(form.costDiscount) >= Number(form.cost)) {
      showToast("Цена со скидкой должна быть меньше основной");
      return;
    }
    if (loading) return;
    try {
      setLoading(true);
      const filtersPayload = Object.entries(form.filters).map(
        ([filterId, value]) => {
          const filter = dynamicFilters.find(f => f.id === filterId);
          const option = filter?.filterParams.find(
            p => p.valueLabel === value
          );
          return {
            filterId,
            value,
            label: option?.label || value
          };
        }
      );

      const payload = {
        isActive: form.isActive,
        category: form.category,
        subcategory: form.subcategory,
        name: form.name,
        description: form.description,
        quantity: Number(form.quantity),
        isCombine: form.isCombine,
        cost: Number(form.cost),
        isDiscount: form.isDiscount,
        costDiscount: form.isDiscount
          ? Number(form.costDiscount)
          : null,
        filters: filtersPayload,
        customParameters: form.customParameters,
        imageIds: images
          .filter(i => i.existing)
          .map(i => i.id)
      };

      let productId = id;

      if (isEdit) {
        await api.put(`/api/seller/products/${id}`, payload);
      } else {
        const { data } = await api.post(
          "/api/seller/products",
          payload
        );
        productId = data.id;
      }

      const newImages = images.filter(i => !i.existing);

      if (newImages.length > 0) {
        const formData = new FormData();

        newImages.forEach(img => {
          formData.append("files", img.file);
        });

        await api.post(
          `/api/seller/products/${productId}/images`,
          formData
        );
      }
      showToast("Товар успешно создан", "info");
      navigate("/seller/products");

    } catch {
      setLoading(false);
      showToast("Ошибка в создании товара");
      console.log("Ошибка создания");
    }
  }

  const categoryOptions = useMemo(() => {
    return categories.map(c => ({
      value: c.id,
      label: c.name
    }));
  }, [categories]);

  const subcategoryOptions = useMemo(() => {
    if (!form.category.length) return [];

    const selectedCategories = categories.filter(c =>
      form.category.includes(c.id)
    );

    const subs = selectedCategories.flatMap(c =>
      c.subcategories.map(s => ({
        value: s.id,
        label: s.name
      }))
    );

    return subs;
  }, [categories, form.category]);

  const dynamicFilters = useMemo(() => {
    if (!form.category?.length) return [];

    return categories
      .filter(c => form.category.includes(c.id))
      .flatMap(c => c.filters);
  }, [form.category, categories]);

  async function loadCategories() {
    try {
      setLoading(true);
      const { data } = await api.get("/api/seller/categories");
      setCategories(data || []);
      setLoading(false);
    } catch {
      console.log("Ошибка загрузки категорий");
    }
  }

  function handleImagesChange(e) {
    const files = Array.from(e.target.files);

    const validFiles = files.filter(file => {
      if (file.size > 1024 * 1024) {
        alert(`Файл ${file.name} превышает 1MB`);
        return false;
      }
      return true;
    });

    const mapped = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setImages(prev => [...prev, ...mapped]);
    e.target.value = null;
  }

  function removeImage(index) {
    setImages(prev => {
      const img = prev[index];

      if (!img.existing) {
        URL.revokeObjectURL(img.preview);
      }

      return prev.filter((_, i) => i !== index);
    });
  }


  function addCustomParameter() {
    setForm(prev => ({
      ...prev,
      customParameters: [
        ...prev.customParameters,
        {
          name: "",
          parameter: "",
          typeParameter: "string"
        }
      ]
    }));
  }

  function updateCustomParameter(index, key, value) {
    setForm(prev => {
      const next = [...prev.customParameters];
      next[index] = {
        ...next[index],
        [key]: value
      };

      return {
        ...prev,
        customParameters: next
      };
    });
  }

  function removeCustomParameter(index) {
    setForm(prev => ({
      ...prev,
      customParameters: prev.customParameters.filter((_, i) => i !== index)
    }));
  }

  useEffect(() => {
    if (!form.category.length) {
      handleChange("subcategory", []);
      return;
    }

    const validSubIds = categories
      .filter(c => form.category.includes(c.id))
      .flatMap(c => c.subcategories.map(s => s.id));

    const filtered = form.subcategory.filter(id =>
      validSubIds.includes(id)
    );

    if (filtered.length !== form.subcategory.length) {
      handleChange("subcategory", filtered);
    }

  }, [form.category, categories]);

  async function loadMarket() {
    const { data } = await api.get("/api/seller/market");
    setMarketPercent(data.percent);
  }
  useEffect(() => {
    loadCategories();
    loadMarket();
    if (id) loadProduct();
  }, [id]);

  async function loadProduct() {
    if (!id) return;

    try {
      setLoading(true);

      const { data } = await api.get(
        `/api/seller/products/${id}/edit`
      );

      setForm({
        isActive: data.isActive,

        category: data.categoryIds ?? [],
        subcategory: data.subcategoryIds ?? [],

        name: data.name,
        description: data.description || "",

        quantity: data.quantity,
        isCombine: data.isCombine,
        status: data.status,
        comment: data.comment,

        cost: data.cost?.toString() ?? "",
        isDiscount: data.isDiscount,
        costDiscount: data.costDiscount
          ? data.costDiscount.toString()
          : "",

        filters:
          data.filters?.reduce((acc, f) => {
            acc[f.filterId] = f.value;
            return acc;
          }, {}) || {},

        customParameters:
          data.customParameters?.map(s => ({
            id: s.id,
            name: s.name,
            parameter: s.parameter
          })) || []
      });

      setImages(
        data.images?.map(p => ({
          id: p.id,
          preview: p.image,
          existing: true
        })) || []
      );

      setLoading(false);
    } catch {
      showToast("Ошибка загрузки товара");
      setLoading(false);
    }
  }

  return (
    <div className="seller-product-form">

      <BackButton />

      <Breadcrumbs
        items={[
          { label: "Главная", path: "/seller/dashboard" },
          { label: "Товары", path: "/seller/products" },
          { label: isEdit ? "Редактирование товара" : "Добавление товара" }
        ]}
      />

      <h2>{isEdit ? "Редактирование товара" : "Добавление товара"}</h2>

      {/* ПРОДАЁТСЯ */}
      <div className="seller-product-form__switch-row">
        {form.status === "check" ? (
          <>
            <Switch value={true} disabled />
            <span>На модерации</span>
          </>
        ) : form.status === "cancel" ? (
          <>
            <Switch value={false} disabled />
            <span>Отклонено</span>
          </>
        ) : (
          <>
            <Switch
              value={form.isActive}
              onChange={(val) => handleChange("isActive", val)}
            />
            <span>Продается</span>
          </>
        )}
      </div>

      {form.status === "cancel" && form.comment && (
        <div className="seller-product-form__moderation-warning">
          <div className="seller-product-form__moderation-title">
            Товар не одобрен к публикации
          </div>
          <div className="seller-product-form__moderation-reason">
            Причина: {form.comment}
          </div>
        </div>
      )}

      {/* КАТЕГОРИЯ */}
      <MultiSearchSelect
        label="Категория"
        value={form.category}
        onChange={(val) => handleChange("category", val)}
        options={categoryOptions}
      />

      <MultiSearchSelect
        label="Подкатегория"
        value={form.subcategory}
        onChange={(val) => handleChange("subcategory", val)}
        options={subcategoryOptions}
      />

      <FloatingInput
        label="Название"
        value={form.name}
        onChange={(e) => handleChange("name", e.target.value)}
      />

      <FloatingTextarea
        label="Описание"
        value={form.description}
        onChange={(e) => handleChange("description", e.target.value)}
      />

      <FloatingInput
        label="Количество (шт)"
        type="number"
        value={form.quantity}
        onChange={(e) => handleChange("quantity", e.target.value)}
      />

      {/* СБОРНЫЙ */}
      <div className="seller-product-form__switch-row">
        <Switch
          value={form.isCombine}
          onChange={(val) => handleChange("isCombine", val)}
        />
        <span>Сборный товар</span>
      </div>

      <FloatingInput
        label="Стоимость"
        type="number"
        value={form.cost}
        onChange={(e) => handleChange("cost", e.target.value)}
      />

      {/* СКИДКА */}
      <div className="seller-product-form__switch-row">
        <Switch
          value={form.isDiscount}
          onChange={(val) => handleChange("isDiscount", val)}
        />
        <span>Сделать скидку</span>
      </div>
      {form.isDiscount && (
        <FloatingInput
          label="Цена со скидкой"
          type="number"
          value={form.costDiscount}
          onChange={(e) =>
            handleChange("costDiscount", e.target.value)
          }
        />
      )}

      <div className="seller-product-form__commission">
        <div>
          Итоговая стоимость для клиента
          <br />
          с учетом комиссии {marketPercent}%:
        </div>

        <div className="seller-product-form__final">
          {finalPrice.toLocaleString("ru-RU")} ₽
        </div>
      </div>

      <div className="seller-product-form__photos">
        <div className="seller-product-form__photos-title">
          Фотографии товара
        </div>

        <div className="seller-product-form__photos-grid">

          {images.map((img, i) => (
            <div key={img.id ?? img.preview} className="seller-product-form__photo">
              {img.existing ? (
                <ApiImage src={img.preview} />
              ) : (
                <img src={img.preview} alt="" />
              )}
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="seller-product-form__photo-remove"
              >
                ×
              </button>
            </div>
          ))}

          <label className="seller-product-form__upload">
            Загрузить фотографии
            <input
              type="file"
              multiple
              accept="image/png, image/jpeg, image/jpg"
              onChange={handleImagesChange}
              hidden
            />
          </label>

        </div>

        <div className="seller-product-form__photos-hint">
          jpg, jpeg, png максимум 1 MB, 1024x1024
        </div>
      </div>
      {dynamicFilters.length > 0 ? (
        <div className="seller-product-form__filters">
          <div className="seller-product-form__title">
            Фильтры
          </div>
          {dynamicFilters.map(filter => (
            <SingleSearchSelect
              key={filter.id}
              label={filter.name}
              value={form.filters[filter.id] || null}
              onChange={(val) =>
                setForm(prev => ({
                  ...prev,
                  filters: {
                    ...prev.filters,
                    [filter.id]: val
                  }
                }))
              }
              options={filter.filterParams.map(p => ({
                value: p.valueLabel,
                label: p.label
              }))}
            />
          ))}
        </div>
      ) : (<></>)}

      {/* СОСТАВ */}
      <div className="seller-product-form__custom">
        <div className="seller-product-form__custom-title">
          Параметры
        </div>

        {form.customParameters.map((param, index) => (
          <div
            key={index}
            className="seller-product-form__custom-item"
          >
            <FloatingInput
              label="Наименование"
              value={param.name}
              onChange={(e) =>
                updateCustomParameter(index, "name", e.target.value)
              }
            />

            <FloatingInput
              label="Параметр"
              value={param.parameter}
              onChange={(e) =>
                updateCustomParameter(index, "parameter", e.target.value)
              }
            />

            <Button
              className="btn-secondary seller-product-form__custom-remove"
              onClick={() => removeCustomParameter(index)}
            >
              <Icon name="trash" color="#A94A4A"></Icon> Удалить параметр
            </Button>
          </div>
        ))}

        <Button
          className="btn-secondary seller-product-form__custom-add"
          onClick={addCustomParameter}
        >
          <Icon name="plus" color="#A94A4A"></Icon> Добавить параметр
        </Button>
      </div>

      <Button
        className="btn-primary large"
        onClick={handleSubmit}
        load={loading}
      >
        {isEdit ? "Сохранить изменения" : "Опубликовать"}
      </Button>

    </div>
  );
}
